import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { mergeDeepRight, mergeWith } from 'ramda';

import { actions } from '@ezugi/bootstrap';
import { GenericPayloadType, GenericStateType } from '@ezugi/types';

import { getTotalBet } from '../epics/bets/utils';

const { betActions: betActionsBootstrap } = actions;

type BetValue = {
    value: number;
}

const addValues = (a: BetValue, b: BetValue) => ({ ...a, ...b, value: +(a.value + b.value).toFixed(2) });

export const INITIAL_STATE: GenericStateType = {
  current: {},
  totalBet: 0,
  history: [ {
    current: {},
    totalBet: 0,
  } ],
};

const betSlice = createSlice({
  name: 'bet',
  initialState: INITIAL_STATE,
  reducers: {
    set({ history, ...state }, { payload: { betIndex, value, ...rest } }: PayloadAction<GenericPayloadType>) {
      const current = mergeWith(addValues, state.current, { [betIndex]: { value, ...rest } });
      const t = getTotalBet(current);

      return {
        ...state,
        current,
        history: [ ...history, { ...state, current, totalBet: t } ],
      };
    },
    reset(state) {
      return {
        ...state,
        ...INITIAL_STATE,
      };
    },
    clear(state) {
      return {
        ...state,
        ...INITIAL_STATE,
        history: [ ...state.history, INITIAL_STATE ],
      };
    },
    cache(state, { payload }: PayloadAction<GenericPayloadType>) {
      return {
        ...state,
        last: payload,
      };
    },
    update(state, { payload }: PayloadAction<GenericPayloadType>) {
      return {
        ...state,
        ...payload,
        history: [ ...state.history, payload ],
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(betActionsBootstrap.history.reset, (state) => ({
      ...state,
      history: [ ...INITIAL_STATE.history ],
    }));
    builder.addCase(betActionsBootstrap.history.update, (state, { payload }: PayloadAction<GenericPayloadType>) => ({
      ...state,
      ...payload,
    }));
    builder.addCase(
      betActionsBootstrap.totalBet.set,
      (state, { payload: { value } }: PayloadAction<GenericPayloadType>) => ({
        ...state,
        totalBet: value,
      })
    );
  },
});

export const betActions = mergeDeepRight(betActionsBootstrap, {
  bet: betSlice.actions,
});

export default betSlice.reducer;
